import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, CircleDollarSign, Coins, Landmark, PiggyBank, Scale, Sparkles, TrendingDown, TrendingUp, Wallet } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import AnalyticsPanel from "../components/AnalyticsPanel";
import ElContaAvatar from "../components/ElContaAvatar";
import ExportMenu from "../components/ExportMenu";
import SmartInput from "../components/SmartInput";
import { api } from "../lib/api";

const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 });
const PERIODS = [
  { key: "day", label: "Día" },
  { key: "week", label: "Semana" },
  { key: "month", label: "Mes" },
  { key: "year", label: "Año" },
];

function deltaFor(current, previous) {
  if (previous === null || previous === undefined) return null;
  const diff = current - previous;
  if (Math.abs(diff) < 0.5 && Math.abs(previous) < 0.5) return { pct: 0, dir: "flat" };
  if (Math.abs(previous) < 0.5) return { pct: null, dir: diff >= 0 ? "up" : "down", raw: diff };
  const pct = (diff / Math.abs(previous)) * 100;
  return { pct, dir: diff > 0 ? "up" : diff < 0 ? "down" : "flat", raw: diff };
}

function DeltaTag({ delta, invertColor = false }) {
  if (!delta) return null;
  const good = invertColor ? delta.dir === "down" : delta.dir === "up";
  const color = delta.dir === "flat" ? "flat" : good ? "good" : "bad";
  const arrow = delta.dir === "up" ? "▲" : delta.dir === "down" ? "▼" : "•";
  const text = delta.pct === null
    ? `${arrow} vs anterior`
    : `${arrow} ${Math.abs(delta.pct).toFixed(0)}% vs anterior`;
  return <span className={`metric-delta ${color}`} data-testid="metric-delta">{text}</span>;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [user, setUser] = useState(null);
  const [period, setPeriod] = useState("month");
  const [offset, setOffset] = useState(0);

  const refresh = () => {
    api.get(`/dashboard?period=${period}&offset=${offset}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData({ income: 0, expenses: 0, available: 0, iva_neto: 0, isr_estimado: 0, total_impuestos: 0, after_tax: 0, tax_regime: "Persona Física", tax_progress: 0, friendly_tip: "Registra tu primer movimiento para comenzar.", period_label: "Mes actual" }));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [period, offset]);
  useEffect(() => { api.get("/auth/me", { withCredentials: true }).then((r) => setUser(r.data)).catch(() => setUser(null)); }, []);

  const d = data || { income: 0, expenses: 0, available: 0, iva_neto: 0, isr_estimado: 0, total_impuestos: 0, after_tax: 0, tax_regime: "Persona Física", tax_progress: 0, friendly_tip: "Preparando tu resumen...", period_label: "Mes actual" };
  const prev = d.previous_period || null;

  const topCards = [
    ["Ingresos", d.income, "mint", TrendingUp, "/ingresos", prev?.income, false],
    ["Egresos", d.expenses, "yellow", TrendingDown, "/gastos", prev?.expenses, true],
    ["Dinero disponible", d.available, d.available >= 0 ? "mint" : "coral", Wallet, null, prev?.available, false],
    ["Efectivo en caja", d.cash_balance || 0, (d.cash_balance || 0) >= 0 ? "sky" : "coral", Coins, "/caja", prev?.cash_balance, false],
  ];
  const bottomCards = [
    ["IVA neto", d.iva_neto, "coral", Scale, prev?.iva_neto, true],
    ["ISR estimado", d.isr_estimado, "coral", Landmark, prev?.isr_estimado, true],
    ["Total impuestos", d.total_impuestos, "coral", PiggyBank, prev?.total_impuestos, true],
    ["Después de impuestos", d.after_tax, d.after_tax >= 0 ? "mint" : "coral", Wallet, prev?.after_tax, false],
  ];

  return (
    <AppShell>
      <div className="page">
        <section className="welcome">
          <div>
            <small className="eyebrow">TU PANEL FINANCIERO</small>
            <h1>
              {new Date().getHours() < 12 ? "Buenos días" : "Buenas tardes"}
              {user?.name ? `, ${user.name}` : ""}.
              <br />
              <em>Todo en orden.</em>
            </h1>
          </div>
          <div className="conta-greeting" data-testid="dashboard-conta-tip">
            <ElContaAvatar small />
            <span>
              El Conta dice:
              <b>“{d.friendly_tip}”</b>
            </span>
          </div>
        </section>
        <section className="dashboard-period" data-testid="dashboard-period-selector">
          <div className="dashboard-period-tabs" role="tablist">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                role="tab"
                aria-selected={period === p.key}
                data-testid={`dashboard-period-${p.key}`}
                className={period === p.key ? "active" : ""}
                onClick={() => { setPeriod(p.key); setOffset(0); }}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="dashboard-period-nav">
            <button
              type="button"
              aria-label="Período anterior"
              data-testid="dashboard-period-prev"
              onClick={() => setOffset((o) => o - 1)}
            >
              <ChevronLeft size={18} />
            </button>
            <span data-testid="dashboard-period-label">{d.period_label || "—"}</span>
            <button
              type="button"
              aria-label="Período siguiente"
              data-testid="dashboard-period-next"
              disabled={offset >= 0}
              onClick={() => setOffset((o) => Math.min(0, o + 1))}
            >
              <ChevronRight size={18} />
            </button>
            {offset !== 0 && (
              <button
                type="button"
                className="dashboard-period-today"
                data-testid="dashboard-period-today"
                onClick={() => setOffset(0)}
              >
                Hoy
              </button>
            )}
          </div>
        </section>
        <section className="dashboard-actions">
          <SmartInput onSaved={refresh} onAddIncome={() => navigate("/ingresos?new=1")} />
          <Link to="/facturacion" className="create-invoice-action" data-testid="dashboard-create-invoice-link">
            Crear Factura
          </Link>
          <ExportMenu resource="dashboard" testid="dashboard-export" label="Exportar resumen" />
        </section>
        <section className="metric-grid" data-testid="dashboard-top-metrics">
          {topCards.map(([label, value, color, Icon, path, prevValue, invert]) => {
            const delta = prev ? deltaFor(value, prevValue) : null;
            return (
              <article
                className={`metric ${color} ${path ? "interactive-summary-card" : ""}`}
                data-testid={`dashboard-metric-${label.toLowerCase().replaceAll(" ", "-")}`}
                key={label}
                onClick={() => path && navigate(path)}
              >
                <Icon size={23} />
                <span>{label}</span>
                <strong>{money.format(value)}</strong>
                <small>{label === "Efectivo en caja" ? "Ingresos − Egresos en efectivo" : path ? "Ver detalle" : "Ingresos − Egresos"}</small>
                <DeltaTag delta={delta} invertColor={invert} />
              </article>
            );
          })}
        </section>
        <section className="metric-grid metric-grid-4" data-testid="dashboard-tax-metrics">
          {bottomCards.map(([label, value, color, Icon, prevValue, invert]) => {
            const delta = prev ? deltaFor(value, prevValue) : null;
            return (
              <article
                className={`metric ${color}`}
                data-testid={`dashboard-metric-${label.toLowerCase().replaceAll(" ", "-")}`}
                key={label}
              >
                <Icon size={22} />
                <span>{label}</span>
                <strong>{money.format(value)}</strong>
                <small>{label === "Total impuestos" ? "IVA + ISR" : label === "Después de impuestos" ? "Disponible − impuestos" : `Régimen ${d.tax_regime}`}</small>
                <DeltaTag delta={delta} invertColor={invert} />
              </article>
            );
          })}
        </section>
        <section className="dashboard-grid">
          <article className="tax-card" data-testid="dashboard-tax-recommendation">
            <div className="tax-head">
              <div>
                <small>RECOMENDACIÓN DE EL CONTA</small>
                <h2>Consejo personalizado</h2>
              </div>
              <CircleDollarSign />
            </div>
            <div className="track">
              <i style={{ width: `${Math.min(d.tax_progress, 100)}%` }} />
            </div>
            <div className="tax-label">
              <b>{d.tax_progress}% de tu ingreso irá al SAT</b>
              <span>Estimación con base en tus movimientos y régimen</span>
            </div>
            <p>
              <Sparkles size={15} />
              {d.friendly_tip}
            </p>
          </article>
        </section>
        <section className="dashboard-cash-flow" data-testid="dashboard-cash-flow">
          <header>
            <div>
              <small className="eyebrow">CAJA · EFECTIVO REAL</small>
              <h2>Tu flujo de dinero físico</h2>
              <p>Los movimientos en efectivo se registran en Caja para mantener un control claro de tus operaciones y facilitar su correcta clasificación fiscal.</p>
            </div>
            <Link to="/caja" className="dashboard-cash-flow-cta" data-testid="dashboard-cash-flow-link">
              <Coins size={16} /> Abrir Caja
            </Link>
          </header>
          <div className="dashboard-cash-flow-grid">
            <article><small>Saldo en caja</small><strong>{money.format(d.cash_balance || 0)}</strong></article>
            <article><small>Ingresos en efectivo</small><strong>{money.format(d.cash_income || 0)}</strong></article>
            <article><small>Egresos en efectivo</small><strong>{money.format(d.cash_expense || 0)}</strong></article>
            <article><small>Solo control interno</small><strong>{money.format((d.cash_internal_income || 0) - (d.cash_internal_expense || 0))}</strong></article>
          </div>
        </section>
        <AnalyticsPanel />
      </div>
    </AppShell>
  );
}
